import type { ComponentChildren, FunctionComponent } from 'preact';
import { useState } from 'preact/hooks';
import cx from 'classnames';
import styles from './ResultsAccordion.module.css';
import { ChevronDown20 } from '../icons/ChevronDown20.js';
import { Error24 } from '../icons/Error24.js';
import { Warning24 } from '../icons/Warning24.js';
import { Success24 } from '../icons/Success24.js';
import { Button } from '../Button/Button.js';

export interface ResultsAccordionProps {
  /** Name of the bundle task or other item (can have formatting, but should not have a header element) */
  name: ComponentChildren;
  /** Index used for generating accordion element IDs */
  index: number;
  result: 'success' | 'warning' | 'error';
  /** Info that goes at the end of the header, inside the clickable area */
  headerEndContent?: string;
  /** Commands to go at the end of the header, outside the clickable area */
  headerCommands?: ComponentChildren;
  /**
   * Get the content, only if the section is expanded.
   * Use `CollapsibleResultsSubSection` for formatting (returning multiple sections if needed).
   *
   * This callback is to prevent rendering thousands of bundle result details unnecessarily.
   */
  getContent: () => ComponentChildren;
}

export interface ResultsAccordionContentProps {
  /** Will be at start of sub-section and styled slightly larger if provided */
  title?: string;
  /** Item name, such as "Input path". Ignored if `itemValue` is not provided. */
  itemName?: string;
  /** Value corresponding to `itemName`, such as the path to a package. Requires `itemName`. */
  itemValue?: ComponentChildren;
  /** Custom children */
  children?: ComponentChildren;
}

const resultIcons = {
  error: <Error24 title="Error" />,
  warning: <Warning24 title="Warning" />,
  success: <Success24 title="Success" />,
};

/**
 * Collapsible section for displaying results of a bundle task or other item.
 * This handles the header formatting and layout, expanding/collapsing, and displaying content.
 */
export const ResultsAccordion: FunctionComponent<ResultsAccordionProps> = (props) => {
  const { name, result, headerEndContent, headerCommands, getContent, index } = props;
  const [isOpen, setIsOpen] = useState<boolean | undefined>(undefined);
  const showContent = isOpen === true || (isOpen === undefined && result === 'error');
  const headerButtonId = `results-accordion-button-${index}`;
  const contentId = `results-accordion-content-${index}`;

  // Trying to follow the accordion pattern: https://www.w3.org/WAI/ARIA/apg/patterns/accordion/
  // except the odd thing is we want to allow a separate click target for a refresh button at the end
  return (
    <div class={styles.root}>
      <div class={cx(styles.header, styles[result], showContent && styles.expanded)}>
        <h3 class={styles.headerButtonWrapper}>
          <Button
            id={headerButtonId}
            class={styles.headerButton}
            variant="custom"
            aria-expanded={showContent}
            aria-controls={contentId}
            onClick={() => setIsOpen(!showContent)}
          >
            {/* With the wrapping button, the chevron is presentation-only */}
            <ChevronDown20 class={cx(styles.expandIcon, !showContent && styles.collapsed)} />
            {resultIcons[result]}
            <div class={styles.title}>{name}</div>
            {headerEndContent && <div class={styles.headerEnd}>{headerEndContent}</div>}
          </Button>
        </h3>
        {headerCommands && <div class={styles.headerCommands}>{headerCommands}</div>}
      </div>
      <div id={contentId} class={showContent ? styles.content : styles.contentHidden}>
        {showContent && getContent()}
      </div>
    </div>
  );
};

/**
 * Sub-section to return from `ResultsAccordionProps.getContent`.
 * If there are no children and no `itemValue`, the section will be ignored.
 */
export const ResultsAccordionContent: FunctionComponent<ResultsAccordionContentProps> = (props) => {
  const { title, itemName, itemValue, children } = props;
  if (!children && !(itemValue && itemName)) {
    return null;
  }

  // Use a fragment so the sub-parts use the parent's flex styling
  return (
    <>
      {title && <h4 class={styles.title}>{title}</h4>}
      {itemName && itemValue ? (
        <div>
          <div class={styles.itemName}>{itemName}</div>
          <div>{itemValue}</div>
        </div>
      ) : null}
      {children}
    </>
  );
};
